<template>
    <VfModal title="Transfer Call" @close="close" @click.stop>
        <template #header> Transfer Call </template>

        <div class="transfer-modal">
            <div v-if="!availableExtensions.length" class="no-extensions">No other extensions available for transfer.</div>

            <!-- One view that grows with the selections: the method appears once a target is
                 chosen, the reason once that method is a native blind transfer. -->
            <template v-else>
                <div class="transfer-field">
                    <label>Transfer to Extension</label>
                    <VfSmartSelect
                        v-model="selectedExtension"
                        :options="availableExtensions"
                        placeholder="Select an extension"
                        :formatter="formatNumber"
                        :disabled="busy || !!pendingBlindTransfer"
                    />
                </div>

                <template v-if="selectedExtension">
                    <p v-if="isModernTalkBackend === undefined" class="transfer-method__loading" role="status">
                        Checking available transfer options…
                    </p>

                    <div v-else class="transfer-method">
                        <button
                            v-if="isModernTalkBackend"
                            type="button"
                            :aria-pressed="method === 'attended'"
                            class="zs-btn transfer-method__option"
                            :class="{ 'is-selected': method === 'attended' }"
                            data-test="transfer-method-attended"
                            :aria-label="`Call ${targetLabel} first, then complete the transfer once they answer`"
                            :disabled="busy || !!pendingBlindTransfer"
                            @click="selectMethod('attended')"
                        >
                            <span class="transfer-method__pill">Suggested</span>
                            <span class="transfer-method__title">Call first</span>
                            <span class="transfer-method__note">Puts the caller on hold while you speak with {{ targetLabel }} first.</span>
                        </button>

                        <button
                            type="button"
                            :aria-pressed="method === 'blind'"
                            class="zs-btn transfer-method__option"
                            :class="{ 'is-selected': method === 'blind' }"
                            data-test="transfer-method-blind"
                            :aria-label="`Transfer the caller to ${targetLabel} immediately`"
                            :disabled="busy || !!pendingBlindTransfer"
                            @click="selectMethod('blind')"
                        >
                            <span class="transfer-method__title">Transfer now</span>
                            <span class="transfer-method__note">Connects the caller to {{ targetLabel }} immediately. You leave the call.</span>
                        </button>
                    </div>

                    <div v-if="showTransferReason" class="transfer-reason">
                        <label class="transfer-reason__label" for="blind-transfer-reason">
                            <span>Transfer reason <span class="transfer-reason__optional">(optional)</span></span>
                            <input
                                id="blind-transfer-reason"
                                :value="transferReason"
                                data-test="blind-transfer-reason-input"
                                class="transfer-reason__input"
                                type="text"
                                maxlength="320"
                                autocomplete="off"
                                placeholder="e.g. Customer needs billing help"
                                :disabled="busy || !!pendingBlindTransfer"
                                @input="changeTransferReason"
                                @keyup.enter="commitBlind"
                            />
                        </label>
                        <p class="transfer-reason__helper">
                            {{ transferReasonCount }}/80 · Available to {{ targetLabel }} in compatible ZynoTalk apps after the transfer.
                        </p>
                    </div>
                </template>
            </template>

            <p v-if="error" class="transfer-method__error" role="alert">{{ error }}</p>

            <p v-if="pendingBlindTransfer" class="transfer-method__loading" role="status">
                {{
                    pendingBlindTransfer.operationId
                        ? 'Confirming the transfer safely. Please don’t start another transfer.'
                        : 'We could not safely confirm the transfer. End the call below if needed.'
                }}
            </p>
        </div>

        <template #footer>
            <button type="button" class="zs-btn" :disabled="busy || !!pendingBlindTransfer" @click="close">Cancel</button>
            <button
                v-if="pendingBlindTransfer && !pendingBlindTransfer.operationId"
                type="button"
                class="zs-btn zs-btn--danger"
                data-test="blind-transfer-unknown-end-call"
                :disabled="busy"
                @click="endAmbiguousBlindTransfer"
            >
                End call
            </button>
            <button v-else type="button" class="zs-btn zs-btn--primary" data-test="transfer-confirm" :disabled="!canConfirm" @click="confirm">
                {{ confirmLabel }}
            </button>
        </template>
    </VfModal>
</template>

<script lang="ts" setup>
import { handleError, uuid, VfModal, VfSmartSelect } from '@zyno-io/vue-foundation';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import type { IExtensionResponse } from '@/openapi-clients-generated/talk';

import { useIsModernTalkBackend } from '@/modules/admin/helpers/talk-backend';
import { softphoneMgr } from '@/modules/talk/shared/softphone.manager';
import { useTalkHelpers } from '@/modules/talk/shared/store';
import { DataCache } from '@/shared/data/cache';

export interface TransferCallResult {
    mode: TransferMethod;
    consultationSessionId?: string;
}

type TransferMethod = 'blind' | 'attended';

const props = defineProps<{
    callback: (result?: TransferCallResult) => void;
    currentExtensionId: string;
    sessionId: string;
}>();

const { formatNumber } = useTalkHelpers();
const isModernTalkBackend = useIsModernTalkBackend();

const extensions = DataCache.getRef('talkExtensions');
const selectedExtension = ref<IExtensionResponse | null>(null);
const method = ref<TransferMethod | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const transferReason = ref('');
const transferReasonCount = computed(() => Array.from(transferReason.value).length);
const pendingBlindTransfer = ref<{
    callId: string;
    destination: string;
    reason: string;
    startedAtMs: number;
    operationId?: string;
    operationConnectionGeneration?: number;
} | null>(null);
let blindTransferRetryTimer: ReturnType<typeof setTimeout> | undefined;
// This remains shorter than Nexus's two-minute terminal operation window.
const BLIND_TRANSFER_REPLAY_DEADLINE_MS = 75_000;

// Filter out the current extension
const availableExtensions = computed(() => {
    if (!extensions.value) return [];
    return extensions.value.filter(ext => ext.id !== props.currentExtensionId && ext.enableSip);
});

const targetLabel = computed(() => {
    const ext = selectedExtension.value;
    if (!ext) return '';
    return ext.name ? `${ext.name} (Ext. ${ext.extension})` : `Ext. ${ext.extension}`;
});

// FusionPBX only ever offers the blind transfer, so there is nothing to choose there.
watch(isModernTalkBackend, isModern => (method.value = isModern === false ? 'blind' : method.value), { immediate: true });

// A stale failure must not be read as a verdict on the new target.
watch(selectedExtension, () => (error.value = null));

// The reason rides along with the native blind transfer; FusionPBX has nowhere to put it.
const showTransferReason = computed(() => method.value === 'blind' && isModernTalkBackend.value === true);

const canConfirm = computed(() => !!selectedExtension.value && !!method.value && !busy.value && !pendingBlindTransfer.value);

const confirmLabel = computed(() => {
    if (method.value === 'attended') return 'Call first';
    if (method.value === 'blind') return 'Transfer now';
    return 'Transfer';
});

function selectMethod(next: TransferMethod) {
    if (busy.value || pendingBlindTransfer.value) return;
    error.value = null;
    if (next !== 'blind') transferReason.value = '';
    method.value = next;
}

async function confirm() {
    if (!canConfirm.value) return;
    await (method.value === 'attended' ? startAttended() : commitBlind());
}

function close() {
    if (!pendingBlindTransfer.value && !busy.value) props.callback();
}

function clearBlindTransferRetry() {
    if (blindTransferRetryTimer !== undefined) clearTimeout(blindTransferRetryTimer);
    blindTransferRetryTimer = undefined;
}

function scheduleBlindTransferRetry() {
    clearBlindTransferRetry();
    blindTransferRetryTimer = setTimeout(() => {
        void retryUnknownBlindTransfer();
    }, 3_000);
}

async function retryUnknownBlindTransfer() {
    const pending = pendingBlindTransfer.value;
    if (!pending?.operationId || busy.value) return;
    busy.value = true;
    try {
        // A backgrounded timer can run much later than its scheduled three seconds. Never send
        // the operation after the shorter client replay window has elapsed.
        if (Date.now() - pending.startedAtMs >= BLIND_TRANSFER_REPLAY_DEADLINE_MS) {
            pendingBlindTransfer.value = { ...pending, operationId: undefined, operationConnectionGeneration: undefined };
            error.value = 'We could not confirm the transfer in time. End the call below if needed.';
            return;
        }
        const replayProof = await softphoneMgr.getTransferReplayProof(props.sessionId);
        if (Date.now() - pending.startedAtMs >= BLIND_TRANSFER_REPLAY_DEADLINE_MS) {
            pendingBlindTransfer.value = { ...pending, operationId: undefined, operationConnectionGeneration: undefined };
            error.value = 'We could not confirm the transfer in time. End the call below if needed.';
            return;
        }
        if (replayProof === undefined) {
            // The retry never left this client. Keep its exact key and re-probe during the
            // remaining window; the first reconnect probe commonly races link recovery.
            scheduleBlindTransferRetry();
            return;
        }
        // A session can be re-homed from a consultation call to a hub call without a WebSocket
        // reconnect. A proof for the old call must never authorize replay against its successor.
        if (replayProof.callId !== pending.callId) {
            pendingBlindTransfer.value = { ...pending, operationId: undefined, operationConnectionGeneration: undefined };
            error.value = 'The call changed before we could safely confirm the transfer. End the call below if needed.';
            return;
        }
        // Do not let a slow capability probe turn into a post-deadline dispatch.
        if (Date.now() - pending.startedAtMs >= BLIND_TRANSFER_REPLAY_DEADLINE_MS) {
            pendingBlindTransfer.value = { ...pending, operationId: undefined, operationConnectionGeneration: undefined };
            error.value = 'We could not confirm the transfer in time. End the call below if needed.';
            return;
        }
        const result = await softphoneMgr.transferCall(
            props.sessionId,
            pending.destination,
            pending.reason,
            pending.operationId,
            replayProof.connectionGeneration,
            pending.callId
        );
        if (result === 'accepted') {
            pendingBlindTransfer.value = null;
            props.callback({ mode: 'blind' });
            return;
        }
        if (result === 'rejected') {
            pendingBlindTransfer.value = null;
            error.value = "The transfer wasn't accepted. You can update the reason and try again.";
            return;
        }
        if (result === 'unknown-unreplayable') {
            pendingBlindTransfer.value = { ...pending, operationId: undefined, operationConnectionGeneration: undefined };
            error.value = 'We could not safely confirm the transfer. End the call below if needed.';
        } else if (result === 'capability-stale') {
            // The retry was not dispatched; re-probe the new stream with the same key.
            scheduleBlindTransferRetry();
        } else {
            scheduleBlindTransferRetry();
        }
    } finally {
        busy.value = false;
    }
}

async function endAmbiguousBlindTransfer() {
    if (!pendingBlindTransfer.value || pendingBlindTransfer.value.operationId || busy.value) return;
    busy.value = true;
    try {
        await softphoneMgr.hangup(props.sessionId);
        pendingBlindTransfer.value = null;
        props.callback();
    } finally {
        busy.value = false;
    }
}

onBeforeUnmount(clearBlindTransferRetry);

function changeTransferReason(event: Event) {
    transferReason.value = Array.from((event.target as HTMLInputElement).value)
        .slice(0, 80)
        .join('');
}

async function commitBlind() {
    if (!selectedExtension.value || busy.value || pendingBlindTransfer.value) return;
    busy.value = true;
    error.value = null;
    try {
        const replayProof = isModernTalkBackend.value === true ? await softphoneMgr.getTransferReplayProof(props.sessionId) : undefined;
        // Do not send a native blind transfer through a stream that cannot prove operation-id
        // forwarding. Otherwise an acknowledgement lost during a mixed rollout is unknowable.
        if (isModernTalkBackend.value === true && replayProof === undefined) {
            error.value = 'Transfer is temporarily unavailable. Reconnect signaling and try again.';
            return;
        }
        const operationId = replayProof === undefined ? undefined : uuid();
        // Bound recovery from the original keyed send, not the later response-unknown result:
        // that first RPC can consume the normal 30-second request timeout.
        const operationStartedAtMs = Date.now();
        const result =
            isModernTalkBackend.value === true
                ? await softphoneMgr.transferCall(
                      props.sessionId,
                      selectedExtension.value.extension,
                      transferReason.value,
                      operationId,
                      replayProof?.connectionGeneration,
                      replayProof?.callId
                  )
                : await softphoneMgr.transferCall(props.sessionId, selectedExtension.value.extension);
        if (result === 'accepted') {
            props.callback({ mode: 'blind' });
        } else if (result === 'unknown' && operationId && replayProof) {
            pendingBlindTransfer.value = {
                callId: replayProof.callId,
                destination: selectedExtension.value.extension,
                reason: transferReason.value,
                startedAtMs: operationStartedAtMs,
                operationId,
                operationConnectionGeneration: replayProof.connectionGeneration
            };
            error.value = 'Connection interrupted while the transfer was being accepted.';
            scheduleBlindTransferRetry();
        } else if (result === 'unknown-unreplayable') {
            pendingBlindTransfer.value = {
                callId: '',
                destination: selectedExtension.value.extension,
                reason: transferReason.value,
                startedAtMs: Date.now()
            };
            error.value = 'The connection changed before we could safely reconcile the transfer. End the call below if needed.';
        } else if (result === 'capability-stale') {
            error.value = 'Signaling changed before the transfer was sent. Reconnect and try again.';
        } else {
            error.value = "Couldn't start the transfer. Please try again.";
        }
    } finally {
        busy.value = false;
    }
}

async function startAttended() {
    if (isModernTalkBackend.value !== true || !selectedExtension.value || busy.value) return;
    busy.value = true;
    error.value = null;
    try {
        const consultationSessionId = await softphoneMgr.startAttendedTransfer(props.sessionId, selectedExtension.value.extension);
        if (consultationSessionId) {
            props.callback({ mode: 'attended', consultationSessionId });
        } else {
            error.value = "Couldn't start the consultation call. Please try again.";
        }
    } catch (err) {
        handleError(err, 'transfer-call:attended');
        error.value = "Couldn't start the consultation call. Please try again.";
    } finally {
        busy.value = false;
    }
}
</script>

<style scoped>
@reference '#tw.css';

.transfer-modal {
    @apply flex flex-col gap-4;
}

.no-extensions {
    @apply text-center py-8 text-ink-3;
}

.transfer-field {
    @apply flex flex-col gap-1 text-sm font-medium text-ink-2;
}

.transfer-method {
    @apply flex flex-col gap-2;
}

.transfer-method__error {
    @apply text-sm text-err;
}

.transfer-method__loading {
    @apply text-sm text-ink-3;
}

.transfer-reason {
    @apply flex flex-col gap-1;
}

.transfer-reason__helper {
    @apply text-sm text-ink-3;
}

.transfer-reason__label {
    @apply flex flex-col gap-1 text-sm font-medium text-ink-2;
}

.transfer-reason__optional {
    @apply font-normal text-ink-3;
}

.transfer-reason__input {
    @apply w-full rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-accent disabled:opacity-60;
}

/* Escapes the global button height clamp so these can stack a pill/title/note. */
.transfer-method__option {
    @apply flex flex-col items-start gap-1 h-auto p-3 text-left whitespace-normal;
}

.transfer-method__option.is-selected {
    @apply border-accent ring-1 ring-accent;
}

.transfer-method__pill {
    @apply inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wide rounded-full bg-accent-soft text-accent;
}

.transfer-method__title {
    @apply text-sm font-semibold;
}

.transfer-method__note {
    @apply text-xs font-normal opacity-80;
}
</style>
